import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import type Map from 'ol/Map';

export interface BoqLayerFilter {
  typeIds: string[];
  catalogIds: string[];
  alignmentOn: boolean;
}

export type BoqGeoJsonGeometry = {
  type: 'LineString' | 'MultiLineString';
  coordinates: number[][] | number[][][];
};

/**
 * Shares the GIS editor OpenLayers map with LRS when both run in the
 * unified geospatial workspace (single component, one map canvas).
 */
@Injectable({ providedIn: 'root' })
export class GeospatialMapBridgeService {
  private readonly mapSubject = new BehaviorSubject<Map | null>(null);
  private readonly boqRouteSubject = new BehaviorSubject<number | null>(null);
  private readonly boqLayerFilterSubject = new BehaviorSubject<BoqLayerFilter | null>(null);
  private readonly boqGeometrySubject = new BehaviorSubject<BoqGeoJsonGeometry | null>(null);

  readonly mapReady$: Observable<Map> = this.mapSubject.asObservable().pipe(
    filter((m): m is Map => m != null)
  );
  readonly boqRouteSelect$ = this.boqRouteSubject.asObservable();
  readonly boqLayerFilter$ = this.boqLayerFilterSubject.asObservable();
  readonly boqGeometry$ = this.boqGeometrySubject.asObservable();

  selectBoqRoute(routeId: number): void {
    this.boqRouteSubject.next(routeId);
  }

  getBoqRouteId(): number | null {
    return this.boqRouteSubject.value;
  }

  setBoqLayerFilter(filter: BoqLayerFilter | null): void {
    this.boqLayerFilterSubject.next(filter);
  }

  getBoqLayerFilter(): BoqLayerFilter | null {
    return this.boqLayerFilterSubject.value;
  }

  setBoqGeometry(geometry: BoqGeoJsonGeometry | null): void {
    this.boqGeometrySubject.next(geometry);
  }

  getBoqGeometry(): BoqGeoJsonGeometry | null {
    return this.boqGeometrySubject.value;
  }

  getMap(): Map | null {
    return this.mapSubject.value;
  }

  registerGisMap(map: Map): void {
    if (!map) return;
    this.mapSubject.next(map);
  }

  clearGisMap(map?: Map): void {
    if (!map || this.mapSubject.value === map) {
      this.mapSubject.next(null);
    }
  }

  refreshMapSize(): void {
    const map = this.mapSubject.value;
    if (!map) return;
    map.updateSize();
    map.renderSync();
  }
}
